import { useEffect, useMemo, useRef, useState } from 'react';

import type { Reference } from '@apollo/client';

import { SearchUtils } from '@spotify/confidence-core-react';
import {
  ChatEventFragment,
  useChatEventCreatedSubscription,
} from '@spotify/confidence-graphql';

import { useChatEvents } from '../useChatEvents';

const DEFAULT_POLL_INTERVAL_MS = 5000;

export type UseCachedChatEventsResult = {
  chatEvents: ChatEventFragment[];
  isLoading: boolean;
  isLoadingNextPage: boolean;
  error?: Error;
  refetch: () => void;
};

export type UseCachedChatEventsOptions = {
  pollInterval?: number;
};

export const useCachedChatEvents = (
  name: string,
  options?: UseCachedChatEventsOptions,
): UseCachedChatEventsResult => {
  const escapedName = SearchUtils.escapeQuery(name);
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS;
  const {
    chatEvents: loadedEvents,
    hasNextPage,
    nextPage,
    isLoading,
    isLoadingNextPage,
    error,
    refetch,
  } = useChatEvents({
    parent: name,
    orderBy: 'createTime:desc',
    pollInterval,
  });

  useChatEventCreatedSubscription({
    variables: { filter: `chatEvent.name:"${escapedName}\\/events"*` },
    skip: !name,
    onData({ data: { data: event }, client }) {
      const chatEvent = event?.chatEventCreated?.chatEvent;
      if (!chatEvent) return;
      if (!chatEventBelongsToChat(chatEvent, name)) return;

      client.cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          chatEvents(
            existing = {},
            { storeFieldName, readField, toReference },
          ) {
            const expectedParentToken = `"parent":"${name}"`;
            if (
              !storeFieldName.includes(expectedParentToken) ||
              !storeFieldName.includes('"orderBy":"createTime:desc"')
            ) {
              return existing;
            }

            const existingEvents = Array.isArray(existing.chatEvents)
              ? existing.chatEvents
              : [];

            if (
              existingEvents.some(
                (existingEvent: Reference) =>
                  readField('name', existingEvent) === chatEvent.name,
              )
            ) {
              return existing;
            }

            const newEventRef = toReference(chatEvent);
            if (!newEventRef) {
              return existing;
            }

            return {
              ...existing,
              __typename:
                existing.__typename ?? 'ConversationsV2ListChatEventsResponse',
              chatEvents: [newEventRef, ...existingEvents],
            };
          },
        },
      });
    },
  });

  const loadedEventsForChat = useMemo(() => {
    const eventsForChat =
      loadedEvents?.filter(event => chatEventBelongsToChat(event, name)) ?? [];
    const sortedDescending = sortChatEventsDescending(eventsForChat);

    return {
      chronological: [...sortedDescending].reverse(),
      sortedDescending,
    };
  }, [loadedEvents, name]);

  const [cachedEvents, setCachedEvents] = useState<ChatEventFragment[]>([]);
  const cachedEventsRef = useRef<ChatEventFragment[]>([]);

  const updateCache = (next: ChatEventFragment[]) => {
    cachedEventsRef.current = next;
    setCachedEvents(next);
  };

  useEffect(() => {
    updateCache([]);
  }, [name]);

  useEffect(() => {
    if (!loadedEventsForChat.chronological.length) return;

    const current = cachedEventsRef.current.filter(event =>
      chatEventBelongsToChat(event, name),
    );
    const { chronological, sortedDescending } = loadedEventsForChat;

    if (current.length === 0) {
      updateCache(chronological);
      if (hasNextPage) void nextPage();
      return;
    }

    const oldestLoadedName = sortedDescending[sortedDescending.length - 1].name;
    const overlapIndex = current.findIndex(e => e.name === oldestLoadedName);

    if (overlapIndex !== -1) {
      const merged = [...current.slice(0, overlapIndex), ...chronological];
      if (
        merged.length === current.length &&
        merged.every((e, i) => e === current[i])
      ) {
        return;
      }
      updateCache(merged);
      return;
    }

    if (hasNextPage) void nextPage();

    // During cumulative backfill the loaded set is a superset of the
    // cache, so just replace with the full chronological list.
    // Otherwise (poll gap-fill) prepend the older cached events.
    const newestLoadedName = sortedDescending[0].name;
    if (current.findIndex(e => e.name === newestLoadedName) !== -1) {
      updateCache(chronological);
      return;
    }

    updateCache([...current, ...chronological]);
  }, [loadedEventsForChat, hasNextPage, name, nextPage]);

  const cachedEventsForChat = cachedEvents.filter(event =>
    chatEventBelongsToChat(event, name),
  );
  const displayedEvents =
    cachedEventsForChat.length > 0
      ? cachedEventsForChat
      : loadedEventsForChat.chronological;

  return {
    chatEvents: displayedEvents,
    isLoading,
    isLoadingNextPage,
    error,
    refetch,
  };
};

function chatEventBelongsToChat(
  chatEvent: ChatEventFragment,
  chatName: string,
) {
  return chatEvent.name.startsWith(`${chatName}/events/`);
}

function sortChatEventsDescending(events: ChatEventFragment[]) {
  return [...events].sort((a, b) => {
    const byTime = b.createTime.localeCompare(a.createTime);
    if (byTime !== 0) {
      return byTime;
    }

    return b.name.localeCompare(a.name);
  });
}
